import "./ReservationPage.css"; 
import HeroTitle from "../../layouts/HeroTitle/HeroTitle";
import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faUsers, faCalendar, faClock, faUtensils, faEnvelope, faPhone, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { createReservation, getUnavailableDates, checkDateAvailability } from '../../api/reservationApi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function ReservationPage() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        reservationDate: '',
        reservationTime: '',
        numberOfPeople: '',
        specialRequests: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [unavailableDates, setUnavailableDates] = useState([]);
    const [isLoadingDates, setIsLoadingDates] = useState(true);

    useEffect(() => {
        fetchUnavailableDates();
    }, []);

    const fetchUnavailableDates = async () => {
        try {
            setIsLoadingDates(true);
            const startDate = getMinDate();
            const maxDate = getMaxDate();
            const dates = await getUnavailableDates(startDate, maxDate);
            setUnavailableDates(dates);
        } catch (error) {
            toast.error('Erreur lors du chargement des dates non disponibles');
        } finally {
            setIsLoadingDates(false);
        }
    };

    const isDateUnavailable = (dateString) => {
        // Vérifie si la date est dans la liste des dates non disponibles
        return unavailableDates.includes(dateString);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        
        try {
            const response = await createReservation(formData);
            toast.success('Votre réservation a été enregistrée avec succès !');
            setIsSubmitted(true);
            // Réinitialiser le formulaire
            setFormData({
                name: '',
                email: '',
                phone: '',
                reservationDate: '',
                reservationTime: '',
                numberOfPeople: '',
                specialRequests: ''
            });
        } catch (error) {
            toast.error(error.message || 'Une erreur est survenue lors de la réservation');
        } finally {
            setIsLoading(false);
        }
    };

    // Validation de la date (min 24h plus tard, max 1 semaine)
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 1);
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 7);

    const formatDate = (date) => {
        return date.toISOString().split('T')[0];
    };

    const isWeekend = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDay();
        return day === 0 || day === 1; // 0 = dimanche, 1 = lundi
    };

    const isDateDisabled = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDay();
        return day === 0 || day === 1;
    };

    const handleDateChange = async (e) => {
        const selectedDate = e.target.value;
        const date = new Date(selectedDate);
        const day = date.getDay();
        const today = new Date();
        const isToday = selectedDate === formatDate(today);

        // Vérification du weekend
        if (day === 0 || day === 1) {
            e.target.value = '';
            setFormData(prevState => ({
                ...prevState,
                reservationDate: ''
            }));
            toast.error('Les réservations ne sont pas possibles le dimanche et le lundi');
            console.log('Les réservations ne sont pas possibles le dimanche et le lundi');
            return;
        }

        // Vérification pour le jour même après 11h
        if (isToday && today.getHours() >= 11) {
            e.target.value = '';
            setFormData(prevState => ({
                ...prevState,
                reservationDate: ''
            }));
            toast.error('Les réservations le jour même ne sont plus possibles après 11h du matin');
            return;
        }

        try {
            const isAvailable = await checkDateAvailability(selectedDate);
            if (!isAvailable) {
                e.target.value = '';
                setFormData(prevState => ({
                    ...prevState,
                    reservationDate: ''
                }));
                toast.error('Cette date n\'est pas disponible pour la réservation');
                return;
            }
            setFormData(prevState => ({
                ...prevState,
                reservationDate: selectedDate
            }));
            toast.success('Date disponible !');
        } catch (error) {
            toast.error('Erreur lors de la vérification de la disponibilité');
        }
    };

    const getMinDate = () => {
        const today = new Date();
        const currentHour = today.getHours();
        
        // Si on est avant 11h du matin, on peut réserver le jour même
        if (currentHour < 11) {
            // Vérifier si aujourd'hui n'est pas dimanche ou lundi
            const dayOfWeek = today.getDay();
            if (dayOfWeek !== 0 && dayOfWeek !== 1) {
                return formatDate(today);
            }
        }
        
        // Sinon, commencer à partir de demain
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        // Si demain est un dimanche ou lundi, on commence à mardi
        while (tomorrow.getDay() === 0 || tomorrow.getDay() === 1) {
            tomorrow.setDate(tomorrow.getDate() + 1);
        }
        
        return formatDate(tomorrow);
    };

    const getMaxDate = () => {
        const maxDate = new Date();
        maxDate.setDate(maxDate.getDate() + 14);
        return formatDate(maxDate);
    };

    const renderConfirmationMessage = () => (
        <div className="confirmation-message">
            <FontAwesomeIcon icon={faCheckCircle} className="confirmation-icon" />
            <h2>Votre réservation a été envoyée avec succès !</h2>
            <p>Nous vous remercions pour votre réservation. Nous vous contacterons dans les plus brefs délais pour confirmer votre demande.</p>
            {/* <button 
                className="new-reservation-button"
                onClick={() => setIsSubmitted(false)}
            >
                Faire une nouvelle réservation
            </button> */}
        </div>
    );

    return (
        <>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <HeroTitle title="RÉSERVATION" />
            <section className="reservation-section">
                <div className="container">
                    <div className="reservation-content">
                        <div className="reservation-info">
                            <h2>Réservez votre table</h2>
                            <p>Pour une expérience inoubliable, réservez votre table dès maintenant. Nous vous accueillerons avec plaisir pour vous faire découvrir notre cuisine.</p>
                            <div className="info-details">
                                <div className="info-item">
                                    <FontAwesomeIcon icon={faPhone} />
                                    <div>
                                        <h3>Téléphone</h3>
                                        <p>02 47 20 50 60</p>
                                    </div>
                                </div>
                                <div className="info-item">
                                    <FontAwesomeIcon icon={faClock} />
                                    <div>
                                        <h3>Horaires</h3>
                                        <p>Du mardi au samedi</p>
                                        <p>12h00 - 15h30</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {isSubmitted ? (
                            renderConfirmationMessage()
                        ) : (
                            <form className="reservation-form" onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <div className="input-wrapper">
                                        <FontAwesomeIcon icon={faUser} />
                                        <input
                                            type="text"
                                            name="name"
                                            placeholder="Votre nom"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <div className="input-wrapper">
                                            <FontAwesomeIcon icon={faEnvelope} />
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder="Votre email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <div className="input-wrapper">
                                            <FontAwesomeIcon icon={faPhone} />
                                            <input
                                                type="tel"
                                                name="phone"
                                                placeholder="Votre téléphone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <div className="input-wrapper">
                                            <FontAwesomeIcon icon={faCalendar} />
                                            <input
                                                type="date"
                                                name="reservationDate"
                                                value={formData.reservationDate}
                                                onChange={handleDateChange}
                                                min={getMinDate()}
                                                max={getMaxDate()}
                                                required
                                            />
                                            {/* <small className="date-info">
                                                Les réservations ne sont pas possibles le dimanche et le lundi
                                            </small> */}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <div className="input-wrapper">
                                            <FontAwesomeIcon icon={faClock} />
                                            <select
                                                name="reservationTime"
                                                value={formData.reservationTime}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Choisir l'heure</option>
                                                <option value="12:00">12:00</option>
                                                <option value="12:30">12:30</option>
                                                <option value="13:00">13:00</option>
                                                <option value="13:30">13:30</option>
                                                <option value="14:00">14:00</option>
                                                <option value="14:30">14:30</option>
                                                <option value="15:00">15:00</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <div className="input-wrapper">
                                        <FontAwesomeIcon icon={faUsers} />
                                        <select
                                            name="numberOfPeople"
                                            value={formData.numberOfPeople}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">----</option>
                                            <option value="1">1 personne</option>
                                            <option value="2">2 personnes</option>
                                            <option value="3">3 personnes</option>
                                            <option value="4">4 personnes</option>
                                            <option value="5">5 personnes</option>
                                            <option value="6">6 personnes</option>
                                            <option value="7">7 personnes</option>
                                            <option value="8">8 personnes</option>
                                            <option value="9">9 personnes</option>
                                            <option value="10">10 personnes</option>
                                            <option value="11">11 personnes</option>
                                            <option value="12">12 personnes</option>
                                            <option value="13">13 personnes</option>
                                            <option value="14">14 personnes</option>
                                            <option value="15">15 personnes</option>                                            
                                        </select>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <textarea
                                        name="specialRequests"
                                        placeholder="Message ou demande spéciale"
                                        value={formData.specialRequests}
                                        onChange={handleChange}
                                        rows="4"
                                    ></textarea>
                                </div>

                                <button 
                                    type="submit" 
                                    className="submit-button"
                                    disabled={isLoading}
                                >
                                    <FontAwesomeIcon icon={faUtensils} />
                                    {isLoading ? 'Envoi en cours...' : 'Réserver ma table'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}

export default ReservationPage;